import { z } from 'zod';
import { kePhoneSchema } from './common.schemas';
import { integrationConfigValuesSchema, integrationDisplayNameSchema, type MessagingProviderSummary } from './integration-config.schemas';

/**
 * SMS providers a deployment can install (Africa's Talking, Textpie). One provider sends at a time: installing
 * one, or switching to one, turns the others off. Driver sign-in codes and app invites go out through it.
 */

export type SmsProviderSummary = MessagingProviderSummary;

/**
 * Installing sends one real text to `testTo` with the new credentials, because neither provider can check them without
 * sending; a refusal saves nothing. The new installation sends from then on and any other is turned off.
 */
export const installSmsIntegrationSchema = z
  .object({
    providerKey: z.string().min(1),
    displayName: integrationDisplayNameSchema,
    config: integrationConfigValuesSchema,
    testTo: kePhoneSchema,
  })
  .strict();
export type InstallSmsIntegrationInput = z.infer<typeof installSmsIntegrationSchema>;

/** A secret left out keeps its stored value; one supplied replaces it. Changed credentials are tested like an install, so `testTo` comes with `config`. */
export const updateSmsIntegrationSchema = z
  .object({
    displayName: integrationDisplayNameSchema.optional(),
    config: integrationConfigValuesSchema.optional(),
    testTo: kePhoneSchema.optional(),
  })
  .strict()
  .refine((input) => !input.config || input.testTo, { message: 'Changed credentials need a number to send the test text to', path: ['testTo'] });
export type UpdateSmsIntegrationInput = z.infer<typeof updateSmsIntegrationSchema>;

/** "Send a test text" on an installed provider, with its saved credentials. */
export const sendSmsTestSchema = z.object({ to: kePhoneSchema }).strict();
export type SendSmsTestInput = z.infer<typeof sendSmsTestSchema>;

export interface SmsIntegration {
  id: string;
  providerKey: string;
  providerName: string;
  displayName: string;
  /** The one installation texts go out through; at most one is. */
  isSending: boolean;
  /** Non-secret values only. */
  config: Record<string, string | number | boolean>;
  /** Names of the secret fields that hold a value: the form shows "Saved", never the value. */
  secretsSet: string[];
  /** When a test text last went out with these credentials. */
  lastTestedAt: string | null;
  createdAt: string;
}
